/** Text-to-speech with the operating system voices (works in browsers and in Electron on Windows). */
import { localeOf } from '../lib/i18n';

export function ttsAvailable(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

function pickVoice(lang: string): SpeechSynthesisVoice | null {
  const voices = window.speechSynthesis.getVoices();
  const norm = (l: string) => l.replace('_', '-').toLowerCase();
  const target = norm(lang);
  return (
    voices.find((v) => norm(v.lang) === target) ??
    voices.find((v) => norm(v.lang).startsWith(target.slice(0, 2))) ??
    null
  );
}

/**
 * Speaks `text` in `lang` (a BCP 47 tag such as 'id-ID', or 'auto' = the current interface language, as stored in the
 * voice preferences).
 */
export function speak(text: string, lang: string, handlers: { onStart?: () => void; onEnd?: () => void } = {}): void {
  if (!ttsAvailable() || !text.trim()) {
    handlers.onEnd?.();
    return;
  }
  if (lang === 'auto') lang = localeOf();
  const synth = window.speechSynthesis;
  synth.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = lang;
  const voice = pickVoice(lang);
  if (voice) utterance.voice = voice;
  utterance.rate = 1.03;
  utterance.pitch = 1.1;
  utterance.onstart = () => handlers.onStart?.();
  utterance.onend = () => handlers.onEnd?.();
  utterance.onerror = () => handlers.onEnd?.();
  synth.speak(utterance);
}

export function stopSpeaking(): void {
  if (ttsAvailable()) window.speechSynthesis.cancel();
}
